<template>
    <AppHead title="New Award Booking Request" />

    <div class="px-4 md:px-8 py-2 md:py-4 max-w-3xl mx-auto">
        <!-- Header -->
        <div class="mb-6">
            <h2 class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                Request a new award booking
            </h2>
            <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">
                Tell us about your trip and our experts will find award flights
                for your points and miles.
            </p>
        </div>

        <!-- Progress -->
        <div class="mb-6">
            <StepIndicator :current-step="currentStep" :total-steps="steps.length" />
            <p class="text-center text-sm font-medium text-slate-600 dark:text-slate-300 mt-2">
                Step {{ currentStep }} of {{ steps.length }} — {{ steps[currentStep - 1].title }}
            </p>
        </div>

        <!-- Step card -->
        <p
            v-if="currentStep < 5"
            class="text-xs text-slate-500 dark:text-slate-400 text-right mb-1.5"
        >
            <span class="text-red-500">*</span> Required
        </p>
        <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-5 md:p-6">
            <StepTrip
                v-if="currentStep === 1"
                :form="form"
                :errors="formErrors"
                :trip-type-options="tripTypeOptions"
            />
            <StepDates v-if="currentStep === 2" :form="form" :errors="formErrors" />
            <StepPreferences
                v-if="currentStep === 3"
                :form="form"
                :errors="formErrors"
                :cabin-options="cabinOptions"
            />
            <StepPoints
                v-if="currentStep === 4"
                :form="form"
                :errors="formErrors"
                :balances-prefilled="!!pointsBalancesPrefill"
            />
            <StepReview
                v-if="currentStep === 5"
                :form="form"
                :errors="formErrors"
                :deposit-amount="depositAmount"
                :fee-schedule="feeSchedule"
                :legacy-perks="legacyPerks"
                :terms-url="termsUrl"
                :trip-type-options="tripTypeOptions"
                :cabin-options="cabinOptions"
            />
        </div>

        <!-- Server error summary on review -->
        <div v-if="currentStep === 5 && errorSummary.length" class="mt-4">
            <Errors :errors="errorSummary" />
        </div>

        <!-- Navigation -->
        <div class="flex items-center justify-between mt-6">
            <button
                v-if="currentStep > 1"
                type="button"
                @click="previousStep"
                class="px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
            >
                ← Back
            </button>
            <span v-else></span>

            <button
                v-if="currentStep < steps.length"
                type="button"
                @click="nextStep"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
                Next
            </button>
            <ActionButton
                v-else
                :label="`Pay $${depositAmount.toFixed(2)} & Submit`"
                :loading="submitting"
                :disabled="!form.terms_accepted"
                button-id="submit-award-booking"
                classes="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                @clicked="submit"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { notify } from 'notiwind';
import { useAwardBookingsStore } from '@/store/award-bookings';
import AppHead from '@/Partials/AppHead.vue';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import StepIndicator from '@/components/ui/step-indicator/step-indicator.vue';
import StepTrip from '@/components/award-bookings/request-form/step-trip.vue';
import StepDates from '@/components/award-bookings/request-form/step-dates.vue';
import StepPreferences from '@/components/award-bookings/request-form/step-preferences.vue';
import StepPoints from '@/components/award-bookings/request-form/step-points.vue';
import StepReview from '@/components/award-bookings/request-form/step-review.vue';

const props = defineProps({
    depositAmount: { type: Number, required: true },
    feeSchedule: { type: Object, required: true },
    legacyPerks: { type: [Object, null], default: null },
    termsUrl: { type: String, required: true },
    tripTypeOptions: { type: Array, required: true },
    cabinOptions: { type: Array, required: true },
    // "Program: balance" lines from the user's tracked rewards, editable
    // in the points step's textarea.
    pointsBalancesPrefill: { type: String, default: '' },
});

const store = useAwardBookingsStore();
const { submitting, validationErrors } = storeToRefs(store);

const steps = [
    { key: 'trip', title: 'Your trip' },
    { key: 'dates', title: 'Dates & flexibility' },
    { key: 'preferences', title: 'Preferences' },
    { key: 'points', title: 'Points & extras' },
    { key: 'review', title: 'Review & pay' },
];

const currentStep = ref(1);

const form = reactive({
    trip_type: null,
    travelers_count: 1,
    home_airport: '',
    willing_to_position: null,
    arrival_airport: '',
    multi_city_stops: '',
    multi_city_stop_count: null,
    departure_date: '',
    departure_date_flexible: null,
    departure_flexibility: '',
    return_date: '',
    return_date_flexible: null,
    return_flexibility: '',
    same_flight_required: null,
    has_travel_restrictions: null,
    travel_restrictions: '',
    preferred_cabins: [],
    min_trip_length_days: null,
    max_trip_length_days: null,
    points_balances: props.pointsBalancesPrefill || '',
    willing_to_purchase_miles: null,
    additional_notes: '',
    promo_code: '',
    terms_accepted: false,
});

const isMultiCity = computed(() =>
    ['multi_city', 'multi_city_extended'].includes(form.trip_type)
);
const isRoundTrip = computed(() => form.trip_type === 'round_trip');

// Which payload fields belong to which step, for jumping to the earliest
// server validation error.
const stepFields = [
    ['trip_type', 'travelers_count', 'home_airport', 'willing_to_position', 'arrival_airport', 'multi_city_stops', 'multi_city_stop_count'],
    ['departure_date', 'departure_date_flexible', 'departure_flexibility', 'return_date', 'return_date_flexible', 'return_flexibility', 'min_trip_length_days', 'max_trip_length_days'],
    ['same_flight_required', 'has_travel_restrictions', 'travel_restrictions', 'preferred_cabins'],
    ['points_balances', 'willing_to_purchase_miles', 'additional_notes', 'promo_code'],
    ['terms_accepted'],
];

// Per-step client errors keyed by field, shaped exactly like the server's
// 422 payload so the inputs render them the same way.
const clientErrors = ref({});

// Server errors merged with the per-step client checks; client messages win
// for a field while the user is still working the step.
const formErrors = computed(() => ({
    ...(validationErrors.value || {}),
    ...clientErrors.value,
}));

const localIsoDate = (date) => {
    const pad = (part) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const isBlank = (value) => value === '' || value === null || value === undefined;

const isWholeNumberBetween = (value, min, max) => {
    const number = Number(value);
    return !isBlank(value) && Number.isInteger(number) && number >= min && number <= max;
};

// Blank optional number inputs go up as null — Number('') would send 0.
const optionalNumber = (value) => (isBlank(value) ? null : Number(value));

// Client-side mirror of StoreAwardBookingRequest's rules, split by step, so
// problems surface on the step they belong to instead of at pay-and-submit.
const stepErrors = (step) => {
    const errors = {};
    const add = (field, message) => {
        errors[field] = [message];
    };

    switch (step) {
        case 1: {
            if (!form.trip_type) {
                add('trip_type', 'Please select a trip type.');
            }
            if (!isWholeNumberBetween(form.travelers_count, 1, 20)) {
                add('travelers_count', 'Enter the number of travelers (1–20).');
            }
            if (!form.home_airport) {
                add('home_airport', 'Please enter your home airport.');
            }
            if (form.willing_to_position === null) {
                add('willing_to_position', 'Please choose yes or no.');
            }
            if (isMultiCity.value) {
                if (!form.multi_city_stops) {
                    add('multi_city_stops', 'Please list the destinations for your multi-city trip.');
                }
                if (
                    form.trip_type === 'multi_city_extended' &&
                    !isWholeNumberBetween(form.multi_city_stop_count, 1, 20)
                ) {
                    add('multi_city_stop_count', 'Enter the total number of stops (1–20).');
                }
            } else if (!form.arrival_airport) {
                add('arrival_airport', 'Please enter your arrival airport.');
            }
            break;
        }
        case 2: {
            if (!form.departure_date) {
                add('departure_date', 'Please choose a departure date.');
            } else if (form.departure_date <= localIsoDate(new Date())) {
                add('departure_date', 'The departure date must be in the future.');
            }
            if (form.departure_date_flexible === null) {
                add('departure_date_flexible', 'Please choose yes or no.');
            }
            if (form.departure_date_flexible && !form.departure_flexibility) {
                add('departure_flexibility', 'Tell us how flexible your departure date is.');
            }
            if (isRoundTrip.value) {
                if (!form.return_date) {
                    add('return_date', 'Please choose a return date.');
                } else if (form.departure_date && form.return_date < form.departure_date) {
                    add('return_date', 'The return date cannot be before the departure date.');
                }
                if (form.return_date_flexible === null) {
                    add('return_date_flexible', 'Please choose yes or no.');
                }
                if (form.return_date_flexible && !form.return_flexibility) {
                    add('return_flexibility', 'Tell us how flexible your return date is.');
                }
            }
            // Trip length is optional (one-way trips have none), but a bound
            // that is given must be sensible.
            const hasMinimumLength = !isBlank(form.min_trip_length_days);
            const hasMaximumLength = !isBlank(form.max_trip_length_days);
            if (hasMinimumLength && !isWholeNumberBetween(form.min_trip_length_days, 1, 365)) {
                add('min_trip_length_days', 'Enter the minimum trip length in days (1–365), or leave it blank.');
            }
            if (hasMaximumLength && !isWholeNumberBetween(form.max_trip_length_days, 1, 365)) {
                add('max_trip_length_days', 'Enter the maximum trip length in days (1–365), or leave it blank.');
            } else if (
                hasMaximumLength &&
                isWholeNumberBetween(form.min_trip_length_days, 1, 365) &&
                Number(form.max_trip_length_days) < Number(form.min_trip_length_days)
            ) {
                add('max_trip_length_days', 'The maximum trip length must be at least the minimum trip length.');
            }
            break;
        }
        case 3: {
            if (form.same_flight_required === null) {
                add('same_flight_required', 'Please choose yes or no.');
            }
            if (form.has_travel_restrictions === null) {
                add('has_travel_restrictions', 'Please choose yes or no.');
            }
            if (form.has_travel_restrictions && !form.travel_restrictions) {
                add('travel_restrictions', 'Tell us which countries you cannot travel through.');
            }
            if (form.preferred_cabins.length === 0) {
                add('preferred_cabins', 'Select at least one preferred cabin.');
            }
            break;
        }
        case 4: {
            if (!form.points_balances) {
                add('points_balances', 'Please share your current points and mileage balances.');
            }
            if (form.willing_to_purchase_miles === null) {
                add('willing_to_purchase_miles', 'Please choose yes or no.');
            }
            break;
        }
        default:
            break;
    }

    return errors;
};

// Clear a field's client error the moment the user fixes it; new problems
// only appear on the next attempt to advance.
watch(form, () => {
    if (Object.keys(clientErrors.value).length === 0) {
        return;
    }
    const current = stepErrors(currentStep.value);
    clientErrors.value = Object.fromEntries(
        Object.entries(current).filter(([field]) => field in clientErrors.value)
    );
});

const nextStep = () => {
    const errors = stepErrors(currentStep.value);
    if (Object.keys(errors).length > 0) {
        clientErrors.value = errors;
        notify({
            group: 'notifications',
            type: 'error',
            title: 'Almost there',
            text: 'Please fill in the highlighted fields to continue.',
        });
        return;
    }
    clientErrors.value = {};
    currentStep.value = Math.min(currentStep.value + 1, steps.length);
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const previousStep = () => {
    clientErrors.value = {};
    currentStep.value = Math.max(currentStep.value - 1, 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const errorSummary = computed(() =>
    Object.values(validationErrors.value || {})
        .flat()
        .map((message) => ({ message }))
);

// Null out fields hidden by the conditional logic so the server never
// receives contradictory answers (e.g. a return date on a one-way trip).
const buildPayload = () => ({
    ...form,
    travelers_count: Number(form.travelers_count),
    min_trip_length_days: optionalNumber(form.min_trip_length_days),
    max_trip_length_days: optionalNumber(form.max_trip_length_days),
    arrival_airport: isMultiCity.value ? null : form.arrival_airport,
    multi_city_stops: isMultiCity.value ? form.multi_city_stops : null,
    multi_city_stop_count:
        form.trip_type === 'multi_city_extended' && form.multi_city_stop_count
            ? Number(form.multi_city_stop_count)
            : null,
    departure_flexibility: form.departure_date_flexible ? form.departure_flexibility : null,
    return_date: isRoundTrip.value ? form.return_date : null,
    return_date_flexible: isRoundTrip.value ? form.return_date_flexible : false,
    return_flexibility:
        isRoundTrip.value && form.return_date_flexible ? form.return_flexibility : null,
    travel_restrictions: form.has_travel_restrictions ? form.travel_restrictions : null,
    additional_notes: form.additional_notes || null,
    promo_code: form.promo_code || null,
});

const submit = async () => {
    clientErrors.value = {};
    try {
        const response = await store.createBooking(buildPayload());
        // Off to Stripe Checkout for the search fee.
        window.location.assign(response.checkout_url);
    } catch (error) {
        if (error.response?.status === 422) {
            const errored = Object.keys(error.response.data.errors || {});
            const stepIndex = stepFields.findIndex((fields) =>
                fields.some((field) => errored.some((name) => name.startsWith(field)))
            );
            if (stepIndex !== -1) {
                currentStep.value = stepIndex + 1;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }
};
</script>
